import React, { useMemo } from 'react';
import { KalenderBegivenhed } from '../../types';
import { getBegivenhederForDato, DANSKE_DAGE_KORT } from '../../utils/dateUtils';
import styles from './CalendarGrid.module.scss';

interface CalendarGridProps {
  year: number;
  month: number; // 0-indexed
  begivenheder: KalenderBegivenhed[];
  onDagValgt?: (dag: Date) => void;
  mini?: boolean;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: KalenderBegivenhed[];
}

function buildCalendarDays(year: number, month: number, begivenheder: KalenderBegivenhed[]): CalendarDay[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // First day of the month
  const firstDay = new Date(year, month, 1);
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0);

  // Day of week for first day: Monday = 0
  // JS: 0=Sun, 1=Mon, ..., 6=Sat
  // Convert: Mon=0, Tue=1, ..., Sun=6
  const firstDayOfWeek = (firstDay.getDay() + 6) % 7;

  const days: CalendarDay[] = [];

  // Days from previous month
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, month, -i);
    const isToday = date.getTime() === today.getTime();
    days.push({
      date,
      isCurrentMonth: false,
      isToday,
      events: getBegivenhederForDato(begivenheder, date),
    });
  }

  // Days of current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d);
    const isToday = date.getTime() === today.getTime();
    days.push({
      date,
      isCurrentMonth: true,
      isToday,
      events: getBegivenhederForDato(begivenheder, date),
    });
  }

  // Days from next month to fill the grid (complete weeks)
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let d = 1; d <= remaining; d++) {
      const date = new Date(year, month + 1, d);
      const isToday = date.getTime() === today.getTime();
      days.push({
        date,
        isCurrentMonth: false,
        isToday,
        events: getBegivenhederForDato(begivenheder, date),
      });
    }
  }

  return days;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  year,
  month,
  begivenheder,
  onDagValgt,
  mini = false,
}) => {
  const days = useMemo(
    () => buildCalendarDays(year, month, begivenheder),
    [year, month, begivenheder]
  );

  return (
    <div className={`${styles.grid} ${mini ? styles.mini : ''}`}>
      {/* Week day headers */}
      <div className={styles.weekdays}>
        {DANSKE_DAGE_KORT.map((dag) => (
          <div key={dag} className={styles.weekday}>
            {dag}
          </div>
        ))}
      </div>

      {/* Calendar days */}
      <div className={styles.days}>
        {days.map((day, index) => (
          <button
            key={index}
            className={`
              ${styles.day}
              ${day.isCurrentMonth ? '' : styles.otherMonth}
              ${day.isToday ? styles.today : ''}
              ${day.events.length > 0 ? styles.hasEvents : ''}
            `}
            onClick={() => onDagValgt && onDagValgt(day.date)}
            disabled={!onDagValgt}
            aria-label={`${day.date.getDate()}. ${day.events.length > 0 ? `${day.events.length} begivenhed${day.events.length !== 1 ? 'er' : ''}` : ''}`}
          >
            <span className={styles.dayNumber}>{day.date.getDate()}</span>
            {!mini && day.events.length > 0 && (
              <div className={styles.dots}>
                {day.events.slice(0, 3).map((evt, i) => (
                  <span
                    key={i}
                    className={`${styles.dot} ${styles[`dot-${evt.type}`]}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
            )}
            {mini && day.events.length > 0 && (
              <div className={styles.miniDot} aria-hidden="true" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default CalendarGrid;
